import upsell from './upsell';
import LocalStorage from 'Core/commons/LocalStorage';
import {
    licencesProducts,
    bonusPacks,
    productsV3Data,
    groupedPacks,
} from 'Config/authenticated/LicencesData';
import { subscriptionUsesLabels } from 'Config/authenticated/dictionary/Pricingv4';
import Subscription from 'api/sellsyaccountmanager/subscription.js';
import {
    computeCart,
    generateQuantity,
    persistCart,
    fetchMetadata,
} from 'api/sellsyaccountmanager';
import { goToUrl } from 'js/vue/helpers';
import { debounce } from 'lodash';

export default {
    namespaced: true,
    state() {
        return {
            ...upsell.state(),
            context: 'renew',
            earlyRenewDate: null,
            earlyRenewCredit: 0,
            localStorage: new LocalStorage('cart', 'renew.save'),
            subscriptionUses: {
                nbActiveUsers: null,
                nbCustomFields: null,
                nbDunnings: null,
                nbPermissionProfiles: null,
                nbPipelines: null,
            },
            // V3 Subscribed Products;
            previousSubcribedProducts: {
                pack_crm: null,
                pack_invoicing: null,
                pack_management: null,
                pack_stock: null,
                pack_support: null,
            },
            previousPlanCode: null,
            legacyProducts: [],
            licenceDistribution: [],
            staffs: [],
            staffOffset: null,
            staffTotal: 0,
            aggregations: [],
            staffLimit: 100,
        };
    },
    getters: {
        ...upsell.getters,
        renewOnOlderPricingVersion: (state) => {
            return state.previousPricingVersion < state.pricingVersion;
        },
        earlyRenewDate: (state) => {
            return state.earlyRenewDate;
        },
        earlyRenewCredit: (state) => {
            return state.earlyRenewCredit;
        },
        isLatestPricingVersion: (state) => {
            return state.previousPricingVersion === 4;
        },
        subscriptionUseLabel: (state) => (key) => {
            return subscriptionUsesLabels[key](state.subscriptionUses[key]);
        },
        subscriptionUses: (state) => {
            return state.subscriptionUses;
        },
        previousSubcribedProducts: (state) => {
            return state.previousSubcribedProducts;
        },
        previousPlanCode: (state) => {
            return state.previousPlanCode;
        },
        totalProductsLicences: (state, getters) => {
            return getters.availablesProducts.map(({ id, code }) => {
                return {
                    id,
                    code,
                    available:
                        getters.products[id].quantity -
                        state.licenceDistribution.filter((licence) => licence.code === code).length,
                    total: getters.products[id].quantity,
                };
            });
        },
        legacyProducts: (state) => {
            return state.legacyProducts;
        },
        licenceDistribution: (state) => {
            return state.licenceDistribution;
        },
        staffs: (state) => {
            return state.staffs;
        },
        staffOffset: (state) => {
            return state.staffOffset;
        },
        staffTotal: (state) => {
            return state.staffTotal;
        },
        staffLimit: (state) => {
            return state.staffLimit;
        },
        staffCount: (state) => {
            return state.staffs.length;
        },
        aggregations: (state) => {
            return state.aggregations;
        },
    },
    mutations: {
        ...upsell.mutations,
        setEarlyRenewDate(state, date) {
            state.earlyRenewDate = date;
        },
        setEarlyRenewCredit(state, credit) {
            state.earlyRenewCredit = credit;
        },
        setSubscriptionUses(state, uses) {
            _.forEach(uses, (use, key) => {
                if (!_.isUndefined(state.subscriptionUses[key])) {
                    state.subscriptionUses[key] = use;
                }
            });
        },
        setSubscribedProducts(state, products) {
            products.forEach((product) => {
                state.previousSubcribedProducts[product.code] = product;
                state.previousSubcribedProducts[product.code].title =
                    productsV3Data[product.code].title;
            });
        },
        setLegacyProducts(state, products) {
            state.legacyProducts = products;
        },
        addStaffs(state, staffs) {
            state.staffs.push(...staffs);
        },
        setStaffOffset: function (state, offset) {
            state.staffOffset = offset;
        },
        setStaffTotal: function (state, total) {
            state.staffTotal = total;
        },
        setLicenceDistribution(state, licenceDistribution) {
            state.licenceDistribution = licenceDistribution;
        },
        setAggregationsLicences(state, aggregations) {
            state.aggregations = aggregations;
        },
        setPreviousPlanCode(state, previousPlanCode) {
            state.previousPlanCode = previousPlanCode;
        },
    },
    actions: {
        ...upsell.actions,
        fetchMetadata({ commit, dispatch, getters }, { id, version }) {
            commit('setComputeLoading', true);
            fetchMetadata(
                getters.customerId,
                [1, 2].includes(version) ? 'renew_legacy' : getters.context,
                getters.termsType,
            ).then((res) => {
                dispatch('setMetadata', res);
                dispatch('fetchCustomerData', id);
            });
        },
        computeCart: debounce(({ state, getters, commit }) => {
            commit('setComputeLoading', true);
            const payload = computeCart(state);
            if (!payload.items.length) {
                commit('setComputeLoading', false);
                return;
            }
            return new Promise((resolve, reject) => {
                SipClient.post(
                    `/customers/${getters.customerId}/compute-cart`,
                    { pricingversion: 4 },
                    {
                        ...payload,
                        country: _.isNil(payload.country) ? 'FR' : payload.country,
                        earlyRenewal: getters.earlyRenewal,
                    },
                )
                    .then((res) => {
                        const products = getters.products;
                        _.each(getters.products, (product, productId) => {
                            if (!res.data.products[productId]) return;
                            products[productId] = {
                                ...products[productId],
                                total: {
                                    monthly: res.data.products[productId].monthly,
                                    yearly: res.data.products[productId].yearly,
                                },
                            };
                        });
                        commit('setTaxRate', res.data.tax_rate);
                        commit('setProducts', products);
                        commit('setCartTotal', {
                            monthly: res.data.monthly,
                            yearly: res.data.yearly,
                        });
                        commit('setVariousTotal');
                        commit('setCartTotalProrated', {
                            monthly: {
                                total_amount: res.data.totalMonthlyProrationTaxesFree,
                                total_tax_included: res.data.totalMonthlyProrationAllInc,
                            },
                            yearly: {
                                total_amount: res.data.totalProrationTaxesFree,
                                total_tax_included: res.data.totalProrationAllInc,
                            },
                        });
                        commit('setEarlyRenewCredit', res.data.credit_note_amount);
                        commit('setLocalStorage', {
                            customerId: getters.customerId,
                            plan: getters.currentPlan,
                            recurrency: getters.recurrency,
                            products: getters.products,
                        });
                        resolve();
                    })
                    .catch((err) => {
                        reject(err);
                    })
                    .finally(() => {
                        commit('setComputeLoading', false);
                    });
            });
        }, 250),
        setPreviousCart({ commit, getters, state }, payload) {
            const products = {
                ..._.cloneDeep(getters.products),
                ..._.cloneDeep(state.previousCart.products),
            };

            // Reset Bonus Packs price
            _.forEach(getters.availablesPacks, (pack) => {
                products[pack.id].customPricing[0].unit_amount = pack.plans.find(
                    (plan) => plan.code === getters.plan,
                ).amountInCents;
            });

            _.forEach(getters.availablesProducts, (product) => {
                const resetPricing = {
                    ...products[product.id].customPricing[0],
                    unit_amount: product.plans.find((plan) => plan.code === getters.plan)
                        .amountInCents,
                    up_to: 'inf',
                    qt: products[product.id].quantity,
                    min: getters.userIsClient ? products[product.id].quantity : 0,
                };
                products[product.id].customPricing = [resetPricing];
            });
            commit('copyToPreviousCart', products);
        },
        async setCart({ commit, dispatch, getters, state }, payload) {
            if (payload.pricingVersion !== state.pricingVersion && !getters.userIsClient) {
                goToUrl(
                    window.sellsyapp.config.globalProperties.$router.resolve({
                        name: 'sam.cart.form.legacy',
                        params: { id: getters.corporationId },
                        query: { id: payload.id },
                    }).href,
                );
            }
            commit('setSource', payload.source);
            commit('setCartId', payload.id);
            if (getters.renewOnOlderPricingVersion)
                dispatch('fetchLicencesDistribution', payload.id);
            dispatch('updatePlan', payload.plan.split('_')[0]);
            dispatch('updateRecurrency', payload.plan.split('_')[1]);
            commit('setPaymentMethod', payload.paymentMethod);
            commit('setPricingVersionRevision', payload.pricingVersionRevision);
            commit('setPricingVersion', payload.pricingVersion);
            commit('setPeriodApplianceStart', payload.activationDate);
            commit('setExpirationDate', payload.expirationDate);
            // Reset default SuiteCRM Licences when Metadata (products) are already set
            if (!!getters.availablesProducts.length) {
                await dispatch('setDefaultSuiteLicence', {
                    products: getters.products,
                    quantity: 0,
                }).then((res) => commit('setProducts', res));
            }
            // Build cart products quantity
            _.each(payload.products, (product, key) =>
                dispatch('updateProductQuantity', {
                    [key]: generateQuantity(product, getters.userIsClient),
                }),
            );
            commit('setComputeLoading', false);
        },
        fetchCorporationStaffs({ state, getters, commit, dispatch }) {
            return new Promise((resolve, reject) => {
                ApiClient.get('/corporations/' + getters.corporationId + '/staffs', {
                    limit: getters.staffLimit,
                    embed: ['predicted_licenses', 'aggregations.count_licenses'],
                    offset: state.staffOffset,
                })
                    .then((response) => {
                        const staffs = response.data.data
                            .filter(
                                (item) =>
                                    !(item.firstname === 'Support' && item.lastname === 'Sellsy'),
                            )
                            .map((staff) => {
                                return {
                                    ...staff,
                                    id: staff.staff_id,
                                };
                            });
                        commit('addStaffs', staffs);

                        commit('setStaffOffset', response.data.pagination.offset);
                        if (getters['staffTotal'] !== response.data.pagination.total) {
                            commit('setStaffTotal', response.data.pagination.total);
                        }

                        const distribStaffs = staffs.map((staff) => {
                            return {
                                staff: staff.id,
                                code:
                                    !getters.licenceDistribution.find(
                                        (distrib) => distrib.staff === staff.id,
                                    ) && getters.cartId
                                        ? null
                                        : staff._embed.predicted_licenses.product_code,
                                productId: staff._embed.predicted_licenses.product_id,
                            };
                        });

                        /**
                         * While LicenceDistribution isn't created, staffs are source of truth.
                         * Otherwise, distribution take priority but must have productId too.
                         */
                        const licenceDistribution = _.unionBy(
                            getters.licenceDistribution,
                            distribStaffs,
                            'staff',
                        ).map((licence) => {
                            return {
                                ...licence,
                                productId: _.isNil(licence.code)
                                    ? staffs.find((staff) => staff.staff_id === licence.staff)
                                        ._embed.predicted_licenses.product_id
                                    : getters.availablesProducts.find(
                                        (product) => product.code === licence.code,
                                    ).id,
                            };
                        });

                        commit(
                            'setAggregationsLicences',
                            response.data.aggregations.count_licenses,
                        );

                        let products = getters.products;
                        const totalAggregations = getters.aggregations.reduce(
                            (acc, val) => acc + val.count,
                            0,
                        );

                        getters.aggregations.forEach((product) => {
                            // ensures there are at least 2 all included packs on load
                            const aggregationCount =
                                product.product_code === 'pack_all_included' &&
                                totalAggregations < 2
                                    ? 2
                                    : product.count;

                            // Cart Product
                            const currentProduct = products[product.product_id];
                            const hasFreeRank = currentProduct.customPricing.find(
                                (rank) => rank.unit_amount === 0,
                            );
                            const minQuantityByDistribution = getters.licenceDistribution.filter(
                                (distribution) => distribution.code === product.product_code,
                            ).length;

                            const customPricing = currentProduct.customPricing.map(
                                (item, indexPrice) => {
                                    // Basically, quantity is equal to aggregation or item quantity when cart is already registered
                                    let itemQuantity = _.isNil(getters.cartId)
                                        ? aggregationCount
                                        : item.qt;

                                    // When a distribution exists, aka a cart edition by CSM, it become the base and minimum quantity
                                    if (
                                        !!minQuantityByDistribution &&
                                        currentProduct.customPricing.length === 1
                                    )
                                        itemQuantity = minQuantityByDistribution;

                                    // But Cart can have more quantity than recommended by the system (aggregrations or distributions), then Cart quantity is use
                                    if (
                                        currentProduct.quantity > minQuantityByDistribution &&
                                        currentProduct.quantity > itemQuantity &&
                                        currentProduct.customPricing.length === 1
                                    )
                                        itemQuantity = currentProduct.quantity;

                                    // Finally, we must substract free licences to our total quantity to get right paid licences
                                    if (!!hasFreeRank) itemQuantity -= hasFreeRank.qt;

                                    // Meanwhile, on free rank, just keep item quantity
                                    if (item.unit_amount === 0) itemQuantity = item.qt;

                                    return {
                                        ...item,
                                        qt: itemQuantity,
                                        min:
                                            !!minQuantityByDistribution && item.unit_amount !== 0
                                                ? minQuantityByDistribution
                                                : itemQuantity,
                                    };
                                },
                            );

                            products = {
                                ...products,
                                [product.product_id]: {
                                    quantity: customPricing
                                        .map((price) => price.qt)
                                        .reduce((acc, val) => acc + val, 0),
                                    customPricing: customPricing,
                                },
                            };
                        });

                        commit('setLicenceDistribution', licenceDistribution);

                        commit('setProducts', products);
                        dispatch('computeCart');

                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },
        fetchCustomerStaffs({ state, getters, commit }) {
            return new Promise((resolve, reject) => {
                ApiClient.get('/staffs', {
                    limit: getters.staffLimit,
                    embed: ['licenses'],
                    offset: state.staffOffset,
                }).then((response) => {
                    try {
                        commit('addStaffs', response.data.data);
                        commit('setStaffOffset', response.data.pagination.offset);
                        if (getters['staffTotal'] !== response.data.pagination.total) {
                            commit('setStaffTotal', response.data.pagination.total);
                        }
                        resolve(response);
                    } catch (err) {
                        reject(err);
                    }
                });
            });
        },
        fetchStaffs({ dispatch, getters }) {
            if (getters.userIsClient) {
                return dispatch('fetchCustomerStaffs');
            } else {
                return dispatch('fetchCorporationStaffs');
            }
        },
        fetchCustomerData({ commit, dispatch, getters }, payload) {
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.get(
                    `/rest/sellsy-account-manager/corporation/${payload}/datas`,
                    { pricingversion: 4 },
                    (response) => {
                        try {
                            if (_.isNil(getters.cartId)) {
                                commit(
                                    'setPaymentMethod',
                                    response.data.billingSettings.paymentMethod,
                                );
                                const planToApply =
                                    response.data.billingSettings.pricingVersion === 4
                                        ? response.data.billingSettings.pricingPlanCode
                                        : response.defaultPlan;
                                dispatch('updatePlan', planToApply?.split('_')[0]);
                                dispatch('updateRecurrency', planToApply?.split('_')[1]);
                            }
                            commit(
                                'setPreviousPlan',
                                response.data.billingSettings.pricingPlanCode,
                            );
                            commit(
                                'setPreviousPaymentMethod',
                                response.data.billingSettings.paymentMethod,
                            );
                            commit(
                                'setServiceEndDate',
                                response.data.billingSettings.serviceEndDate,
                            );
                            commit(
                                'setEarlyRenewDate',
                                getters.earlyRenewal
                                    ? null
                                    : moment(response.data.billingSettings.serviceEndDate)
                                        .add(1, 'day')
                                        .format(),
                            );
                            commit(
                                'setPeriodApplianceStart',
                                response.data.billingSettings.serviceStartDate,
                            );
                            commit(
                                'setPreviousPricingVersion',
                                response.data.billingSettings.pricingVersion,
                            );
                            commit('setAccountLevel', response.data.levelReadable);
                            commit('setPreviousPlanCode', response.billingSettings.pricingPlanCode);

                            switch (response.data.billingSettings.pricingVersion) {
                                case 4:
                                    dispatch(
                                        'updatePreviousProducts',
                                        response.subscribedProducts.filter(
                                            (product) =>
                                                Object.keys({
                                                    ...licencesProducts,
                                                    ...bonusPacks,
                                                    ...groupedPacks,
                                                }).includes(product.code) &&
                                                !_.isNil(product.productPlan),
                                        ),
                                    );
                                    dispatch('setPreviousCart', response);
                                    break;
                                case 3:
                                    response.subscribedProducts
                                        .filter(
                                            (product) =>
                                                Object.keys(bonusPacks).includes(product.code) &&
                                                !_.isNil(product.productPlan),
                                        )
                                        .forEach((subProduct) => {
                                            const productId = subProduct.productPlan.product.uuid;
                                            dispatch('updateProductQuantity', {
                                                [productId]: {
                                                    customPricing: [
                                                        {
                                                            qt: 1,
                                                            unit_amount:
                                                                getters.cartId &&
                                                                !!getters.products[productId]
                                                                    ? getters.products[productId]
                                                                        .customPricing[0]
                                                                        .unit_amount
                                                                    : getters.availablesPacks
                                                                        .find(
                                                                            (pack) =>
                                                                                pack.id ===
                                                                                  productId,
                                                                        )
                                                                        .plans.find(
                                                                            (plan) =>
                                                                                plan.code ===
                                                                                  getters.plan,
                                                                        ).amountInCents,
                                                            up_to: 'inf',
                                                        },
                                                    ],
                                                    quantity: 0,
                                                },
                                            });
                                        });
                                    break;
                                case 2:
                                case 1:
                                    // In Sale Offer, cart may not have Stock Module but always in SelfService
                                    const createCustomPricing = (module) => {
                                        return [
                                            {
                                                qt:
                                                    (getters.isSourceSale &&
                                                        getters.userIsClient) ||
                                                    (!getters.userIsClient && !!getters.cartId)
                                                        ? getters.products[module.id].quantity
                                                        : 1,
                                                unit_amount:
                                                    (getters.isSourceSale &&
                                                        getters.userIsClient) ||
                                                    (!getters.userIsClient && !!getters.cartId)
                                                        ? getters.products[module.id]
                                                            .customPricing[0].unit_amount
                                                        : module.plans.find(
                                                            (plan) => plan.code === getters.plan,
                                                        ).amountInCents,
                                                up_to: 'inf',
                                            },
                                        ];
                                    };

                                    if (response.hasStockModule) {
                                        const stockModule = getters.availablesPacks.find(
                                            (pack) => pack.code === 'pack_stock',
                                        );
                                        dispatch('updateProductQuantity', {
                                            [stockModule.id]: {
                                                customPricing: createCustomPricing(stockModule),
                                                quantity: 0,
                                            },
                                        });
                                    }

                                    if (response.hasSupportModule) {
                                        const supportModule = getters.availablesPacks.find(
                                            (pack) => pack.code === 'pack_support',
                                        );
                                        dispatch('updateProductQuantity', {
                                            [supportModule.id]: {
                                                customPricing: createCustomPricing(supportModule),
                                                quantity: 0,
                                            },
                                        });
                                    }
                                    break;
                                default:
                                    break;
                            }
                            commit('setLegacyProducts', response.legacyProducts);
                            dispatch('computeCart');
                            commit(
                                'setSubscribedProducts',
                                response.subscribedProducts.filter(
                                    (product) =>
                                        Object.keys(productsV3Data).includes(product.code) &&
                                        !_.isNil(product.productPlan),
                                ),
                            );
                            if (response.data.billingSettings.pricingVersion === 4) {
                                dispatch('computePreviousCart');
                            } else {
                                if (_.isNil(getters.cartId)) dispatch('fetchStaffs');
                                commit('setPreviousCartTotal', {
                                    monthly: {
                                        total_amount_tax_included: response.totalAmountPerMonth,
                                    },
                                    yearly: {
                                        total_amount_tax_included: response.totalAmountPerYear,
                                    },
                                });
                            }
                            resolve(response);
                        } catch (err) {
                            Push.error(
                                window.i18n.__('Problème lors de la récupération des données'),
                            );
                            reject(err);
                        }
                    },
                    (err) => reject(err),
                );
            });
        },
        fetchLicencesDistribution({ commit, dispatch }, cartId) {
            Subscription.getLicencesDistribution(cartId).then((response) => {
                commit('setLicenceDistribution', response);
                dispatch('fetchStaffs');
            });
        },
        createDistribution({ getters }) {
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.post(
                    '/rest/sellsy-account-manager/cart/' +
                        getters.cartId +
                        '/licenses-distributions',
                    {},
                    getters.licenceDistribution.filter((distribution) => !!distribution.code),
                    (response) => {
                        if (response.error) {
                            Push.error(
                                window.i18n.__(
                                    "Nous avons rencontré un problème lors de l'enregistrement de la configuration",
                                ),
                            );
                            reject(response);
                        }
                        resolve(response);
                    },
                );
            });
        },
        createCart({ state, commit, getters, dispatch }) {
            commit('setComputeLoading', true);
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.post(
                    `/rest/sellsy-account-manager/cart`,
                    {},
                    persistCart(state),
                    async (res) => {
                        if (res.error) reject(res);
                        commit('setComputeLoading', false);
                        commit('setCartId', res.id);
                        if (getters.renewOnOlderPricingVersion && !getters.userIsClient) {
                            await dispatch('createDistribution');
                        }
                        resolve(res);
                    },
                    (err) => {
                        commit('setComputeLoading', false);
                        reject(err.response.data.error.message);
                    },
                );
            });
        },
        updateCart({ state, commit, getters, dispatch }, payload) {
            commit('setComputeLoading', true);
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.put(
                    `/rest/sellsy-account-manager/cart/${payload}`,
                    { user_context: getters.userContext },
                    persistCart(state),
                    async (res) => {
                        commit('setComputeLoading', false);
                        if (getters.renewOnOlderPricingVersion && !getters.userIsClient) {
                            await dispatch('createDistribution');
                        }
                        resolve();
                    },
                    (err) => {
                        commit('setComputeLoading', false);
                        reject(err.response.data.error.message);
                    },
                );
            });
        },
        updateProductQuantity({ commit, getters, dispatch }, payload) {
            const productId = Object.keys(payload)[0];
            const distributedProducts = getters.licenceDistribution.filter(
                (item) => item.code !== null,
            );
            const requiredQuantity =
                distributedProducts.filter((item) => item.productId === productId).length ?? 0;
            let products = {
                ...getters.products,
                [productId]: {
                    ...payload[productId],
                    customPricing: [
                        ...payload[productId].customPricing.map((item) => {
                            const freeQuantity =
                                payload[productId].customPricing[0].unit_amount === 0
                                    ? payload[productId].customPricing[0].qt
                                    : 0;
                            return {
                                ...item,
                                qt:
                                    item.up_to === 'inf' &&
                                    payload[productId].quantity < requiredQuantity - freeQuantity
                                        ? requiredQuantity - freeQuantity
                                        : item.qt,
                                min: getters.userIsClient
                                    ? item.min
                                    : item.up_to === 'inf' && requiredQuantity - freeQuantity >= 0
                                        ? requiredQuantity - freeQuantity
                                        : 0,
                            };
                        }),
                    ],
                },
            };

            products = {
                ...products,
                [productId]: {
                    ...products[productId],
                    quantity: products[productId].customPricing.reduce(
                        (acc, val) => acc + val.qt,
                        0,
                    ),
                },
            };

            commit('setProducts', _.cloneDeep(products));
            dispatch('computeCart');
        },
        updateLicencesQuantity({ getters, commit, dispatch }, { value, staff }) {
            let oldProductCode;
            const newDistribution = getters.licenceDistribution.map((licence) => {
                if (licence.staff === staff.id) {
                    oldProductCode = licence.code;
                    licence.code = value;
                    licence.productId = getters.availablesProducts.find(
                        (item) => item.code === value,
                    )?.id;
                }
                return licence;
            });
            commit('setLicenceDistribution', newDistribution);

            let products = getters.products;
            getters.aggregations.forEach((product) => {
                const currentProduct = products[product.product_id];
                products = {
                    ...products,
                    [product.product_id]: {
                        customPricing: currentProduct.customPricing.map((item) => {
                            // Substract free licences quantity to get exact paid licences prediction
                            const productQuantity =
                                item.up_to === 'inf' &&
                                currentProduct.customPricing[0].unit_amount === 0 &&
                                currentProduct.quantity - currentProduct.customPricing[0].qt >= 0
                                    ? currentProduct.quantity - currentProduct.customPricing[0].qt
                                    : item.qt;
                            const itemQt =
                                product.product_code === value && item.unit_amount !== 0
                                    ? productQuantity + 1
                                    : product.product_code === oldProductCode &&
                                      item.unit_amount !== 0 &&
                                      productQuantity > 0
                                        ? productQuantity - 1
                                        : productQuantity;
                            const freeQuantity =
                                currentProduct.customPricing[0].unit_amount === 0
                                    ? currentProduct.customPricing[0].qt
                                    : 0;
                            const min =
                                getters.licenceDistribution.filter(
                                    (distribution) => distribution.code === product.product_code,
                                ).length -
                                    freeQuantity >=
                                0
                                    ? getters.licenceDistribution.filter(
                                        (distribution) =>
                                            distribution.code === product.product_code,
                                    ).length - freeQuantity
                                    : 0;

                            return {
                                ...item,
                                // Allow CSM to add non attributed licences
                                qt: itemQt,
                                min,
                            };
                        }),
                    },
                };
                products = {
                    ...products,
                    [product.product_id]: {
                        ...products[product.product_id],
                        quantity: products[product.product_id].customPricing.reduce(
                            (acc, val) => acc + val.qt,
                            0,
                        ),
                    },
                };
            });

            commit('setProducts', _.cloneDeep(products));
            dispatch('computeCart');
        },
    },
};
